/**
 * Nosto-HitStore.js
 *
 * Almacena e indexa los hits devueltos por Nosto para recuperarlos
 * posteriormente durante eventos de tracking como clics o add to cart.
 *
 * Permite buscar productos usando distintos formatos de ID, incluyendo
 * IDs numéricos de Shopify y Global IDs con formato gid://shopify/Product/.
 *
 * Este archivo puede utilizarse tanto en PLP como en Search y no contiene
 * lógica específica de controllers, DOM, product cards ni llamadas a Nosto.
 */

(function initializeNostoHitStore(global) {
    "use strict";

    class NostoHitStore {
        /**
         * Crea una instancia independiente para almacenar hits de Nosto.
         */
        constructor({
            productIdFields = [
                "productId",
                "product_id",
                "id",
            ],
            debug = false,
        } = {}) {
            this.productIdFields = Array.isArray(productIdFields)
                ? productIdFields
                : ["productId", "product_id", "id"];

            this.debug = Boolean(debug);

            this.hitsByKey = new Map();
            this.primaryKeyByHit = new WeakMap();
        }

        /**
         * Escribe información en consola cuando el modo debug está activo.
         */
        log(...args) {
            if (!this.debug) return;

            console.info("[NostoHitStore]", ...args);
        }

        /**
         * Convierte un identificador en una cadena limpia.
         */
        normalizeString(value) {
            return String(value ?? "").trim();
        }

        /**
         * Extrae el identificador numérico final desde un Global ID de Shopify.
         */
        extractNumericId(value) {
            const normalizedValue = this.normalizeString(value);

            if (!normalizedValue) return "";

            if (normalizedValue.includes("/")) {
                return normalizedValue
                    .split("/")
                    .filter(Boolean)
                    .at(-1);
            }

            return normalizedValue;
        }

        /**
         * Convierte un identificador de producto en un Global ID de Shopify.
         */
        createShopifyGid(value) {
            const numericId = this.extractNumericId(value);

            if (!numericId) return "";

            return `gid://shopify/Product/${numericId}`;
        }

        /**
         * Devuelve todas las variantes de clave válidas para un ID de producto.
         */
        createProductKeys(productId) {
            const rawId = this.normalizeString(productId);

            if (!rawId) return [];

            const numericId = this.extractNumericId(rawId);
            const gid = this.createShopifyGid(rawId);

            return Array.from(
                new Set(
                    [
                        rawId,
                        numericId,
                        gid,
                    ].filter(Boolean)
                )
            );
        }

        /**
         * Busca el identificador principal de producto dentro de un hit de Nosto.
         */
        getProductIdFromHit(hit) {
            if (!hit || typeof hit !== "object") {
                return "";
            }

            for (const field of this.productIdFields) {
                const value = hit[field];

                if (value != null && value !== "") {
                    return this.normalizeString(value);
                }
            }

            return "";
        }

        /**
         * Devuelve todos los posibles IDs relacionados con un hit.
         */
        getProductIdsFromHit(hit) {
            if (!hit || typeof hit !== "object") {
                return [];
            }

            const ids = new Set();

            for (const field of this.productIdFields) {
                const value = hit[field];

                if (value == null || value === "") continue;

                for (const key of this.createProductKeys(value)) {
                    ids.add(key);
                }
            }

            return Array.from(ids);
        }

        /**
         * Comprueba si un objeto puede almacenarse como hit.
         */
        isValidHit(hit) {
            return (
                Boolean(hit) &&
                typeof hit === "object" &&
                Boolean(this.getProductIdFromHit(hit))
            );
        }

        /**
         * Indexa un hit utilizando todas sus posibles variantes de ID.
         */
        add(hit) {
            if (!this.isValidHit(hit)) {
                this.log(
                    "Se ignoró un hit porque no contiene un productId válido.",
                    hit
                );

                return false;
            }

            const productIds = this.getProductIdsFromHit(hit);
            const primaryProductId = this.getProductIdFromHit(hit);

            for (const productId of productIds) {
                this.hitsByKey.set(productId, hit);
            }

            this.primaryKeyByHit.set(
                hit,
                this.extractNumericId(primaryProductId)
            );

            this.log("Hit indexado.", {
                productId: primaryProductId,
                keys: productIds,
                hit,
            });

            return true;
        }

        /**
         * Indexa una lista de hits y devuelve cuántos se almacenaron correctamente.
         */
        addMany(hits = []) {
            if (!Array.isArray(hits)) {
                return 0;
            }

            let addedCount = 0;

            for (const hit of hits) {
                if (this.add(hit)) {
                    addedCount += 1;
                }
            }

            return addedCount;
        }

        /**
         * Reemplaza todo el contenido del store por una nueva lista de hits.
         */
        replace(hits = []) {
            this.clear();

            return this.addMany(hits);
        }

        /**
         * Recupera un hit utilizando cualquier formato compatible de productId.
         */
        get(productId) {
            const keys = this.createProductKeys(productId);

            for (const key of keys) {
                const hit = this.hitsByKey.get(key);

                if (hit) {
                    return hit;
                }
            }

            return null;
        }

        /**
         * Recupera varios hits a partir de una lista de IDs de producto.
         */
        getMany(productIds = []) {
            if (!Array.isArray(productIds)) {
                return [];
            }

            return productIds
                .map((productId) => this.get(productId))
                .filter(Boolean);
        }

        /**
         * Comprueba si existe un hit asociado a un producto.
         */
        has(productId) {
            return Boolean(this.get(productId));
        }

        /**
         * Recupera el ID principal registrado para un hit.
         */
        getPrimaryProductId(hit) {
            if (!hit || typeof hit !== "object") {
                return "";
            }

            return (
                this.primaryKeyByHit.get(hit) ||
                this.extractNumericId(
                    this.getProductIdFromHit(hit)
                )
            );
        }

        /**
         * Elimina un hit y todas las claves que apuntan hacia él.
         */
        remove(productId) {
            const hit = this.get(productId);

            if (!hit) {
                return false;
            }

            for (const [key, storedHit] of this.hitsByKey.entries()) {
                if (storedHit === hit) {
                    this.hitsByKey.delete(key);
                }
            }

            this.primaryKeyByHit.delete(hit);

            this.log("Hit eliminado.", {
                productId,
                hit,
            });

            return true;
        }

        /**
         * Elimina todos los hits almacenados.
         */
        clear() {
            this.hitsByKey.clear();
            this.primaryKeyByHit = new WeakMap();

            this.log("Todos los hits fueron eliminados.");
        }

        /**
         * Devuelve la cantidad real de hits únicos almacenados.
         */
        get size() {
            return this.getAll().length;
        }

        /**
         * Devuelve todos los hits únicos almacenados.
         */
        getAll() {
            return Array.from(
                new Set(this.hitsByKey.values())
            );
        }

        /**
         * Devuelve todos los IDs principales de productos almacenados.
         */
        getProductIds() {
            return this.getAll()
                .map((hit) => this.getPrimaryProductId(hit))
                .filter(Boolean);
        }

        /**
         * Devuelve una representación serializable del contenido para depuración.
         */
        toJSON() {
            return this.getAll().map((hit) => ({
                productId: this.getPrimaryProductId(hit),
                hit,
            }));
        }
    }

    global.NostoHitStore = NostoHitStore;
})(window);